import React, { useEffect, useState } from 'react';
import { Capacitor } from '@capacitor/core';
import { Camera, CameraResultType, CameraSource } from '@capacitor/camera';

interface NativeCameraProps {
    isOpen: boolean;
    onClose: () => void;
    onCapture: (file: File) => void;
    onOpenGallery?: () => void;
    onInternalModalStateChange?: (isOpen: boolean) => void;
    captureMode?: 'image' | 'video' | 'both';
}

function base64ToFile(base64: string, mimeType: string, filename: string): File {
    const base64Data = base64.includes(',') ? base64.split(',')[1] : base64;
    const byteString = atob(base64Data);
    const ab = new ArrayBuffer(byteString.length);
    const ia = new Uint8Array(ab);
    for (let i = 0; i < byteString.length; i++) {
        ia[i] = byteString.charCodeAt(i);
    }
    return new File([ab], filename, { type: mimeType });
}

async function urlToFile(url: string, mimeType: string, filename: string): Promise<File> {
    const response = await fetch(url);
    const blob = await response.blob();
    return new File([blob], filename, { type: mimeType || blob.type });
}

export const NativeCamera: React.FC<NativeCameraProps> = ({
    isOpen,
    onClose,
    onCapture,
    onOpenGallery,
    onInternalModalStateChange,
    captureMode = 'both',
}) => {
    const isNative = Capacitor.isNativePlatform();
    const [showChoice, setShowChoice] = useState(false);
    const [activeCaptureMode, setActiveCaptureMode] = useState<'image' | 'video' | 'both' | null>(null);

    // Trigger state change tracking
    useEffect(() => {
        if (onInternalModalStateChange) {
            onInternalModalStateChange(isOpen);
        }
    }, [isOpen, onInternalModalStateChange]);

    useEffect(() => {
        if (!isOpen) {
            setShowChoice(false);
            setActiveCaptureMode(null);
            return;
        }

        const isIOS = Capacitor.getPlatform() === 'ios' || (typeof navigator !== 'undefined' && /iPad|iPhone|iPod/.test(navigator.userAgent));

        // If on Android/Web and in 'both' mode, show choice modal first so we can open camera directly in that mode
        if (captureMode === 'both' && !isIOS && !activeCaptureMode) {
            setShowChoice(true);
            return;
        }

        const mode = activeCaptureMode || captureMode;
        if (!mode) return;

        if (isNative && mode === 'image') {
            const openNativeCapacitorCamera = async () => {
                try {
                    const photo = await Camera.getPhoto({
                        source: CameraSource.Camera,
                        resultType: CameraResultType.Uri,
                        quality: 100,
                        allowEditing: false,
                        saveToGallery: false,
                        correctOrientation: true,
                    });
                    const mimeType = photo.format === 'jpeg' ? 'image/jpeg' : `image/${photo.format}`;
                    const filename = `capture-${Date.now()}.${photo.format}`;
                    let file: File;
                    if (photo.base64String) {
                        file = base64ToFile(photo.base64String, mimeType, filename);
                    } else if (photo.webPath) {
                        file = await urlToFile(photo.webPath, mimeType, filename);
                    } else if (photo.dataUrl) {
                        file = base64ToFile(photo.dataUrl, mimeType, filename);
                    } else {
                        throw new Error('No usable photo data returned');
                    }
                    onCapture(file);
                    onClose();
                } catch (err: any) {
                    console.error('[NativeCamera] Photo capture error:', err);
                    onClose();
                }
            };
            openNativeCapacitorCamera();
        } else {
            let inputClicked = false;
            const input = document.createElement('input');
            input.type = 'file';
            if (mode === 'video') {
                input.accept = 'video/*';
                input.setAttribute('capture', 'environment');
            } else if (mode === 'image') {
                input.accept = 'image/*';
                input.setAttribute('capture', 'environment');
            } else {
                // 'both' on iOS opens native camera with both tabs directly when capture="environment" is set
                input.accept = 'image/*,video/*';
                input.setAttribute('capture', 'environment');
            }

            let changeFired = false;

            const handleFileSelected = (e: Event) => {
                changeFired = true;
                const files = (e.target as HTMLInputElement).files;
                if (files && files.length > 0) {
                    onCapture(files[0]);
                }
                onClose();
            };

            const handleCancel = () => {
                onClose();
            };

            const handleWindowFocus = () => {
                // When window regains focus (e.g. user dismissed camera/file sheet),
                // check if the change event was fired. If not, treat as cancelled.
                setTimeout(() => {
                    if (!changeFired) {
                        onClose();
                    }
                }, 400);
            };

            input.addEventListener('change', handleFileSelected);
            input.addEventListener('cancel', handleCancel);
            window.addEventListener('focus', handleWindowFocus);
            
            if (!inputClicked) {
                inputClicked = true;
                input.click();
            }

            return () => {
                input.removeEventListener('change', handleFileSelected);
                input.removeEventListener('cancel', handleCancel);
                window.removeEventListener('focus', handleWindowFocus);
            };
        }
    }, [isOpen, isNative, captureMode, activeCaptureMode]);

    if (!isOpen) return null;

    if (showChoice) {
        return (
            <div 
                className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/70 backdrop-blur-md animate-fade-in"
                onClick={() => { setShowChoice(false); onClose(); }}
            >
                <div 
                    className="bg-gray-900/90 border border-white/10 p-6 rounded-3xl w-full max-w-xs text-center shadow-2xl flex flex-col gap-6"
                    onClick={(e) => e.stopPropagation()}
                >
                    <h3 className="text-white text-lg font-bold">Capture Media</h3>
                    <div className="flex flex-col gap-3">
                        <button
                            onClick={() => {
                                setShowChoice(false);
                                setActiveCaptureMode('image');
                            }}
                            className="flex items-center justify-center gap-3 w-full py-4 bg-white/10 hover:bg-white/20 text-white font-bold rounded-2xl border border-white/5 active:scale-95 transition-all"
                        >
                            <svg className="w-6 h-6 text-blue-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 9a2 2 0 012-2h.93a2 2 0 001.664-.89l.812-1.22A2 2 0 0110.07 4h3.86a2 2 0 011.664.89l.812 1.22A2 2 0 0018.07 7H19a2 2 0 012 2v9a2 2 0 01-2 2H5a2 2 0 01-2-2V9z" />
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 13a3 3 0 11-6 0 3 3 0 016 0z" />
                            </svg>
                            Take Photo
                        </button>
                        <button
                            onClick={() => {
                                setShowChoice(false);
                                setActiveCaptureMode('video');
                            }}
                            className="flex items-center justify-center gap-3 w-full py-4 bg-white/10 hover:bg-white/20 text-white font-bold rounded-2xl border border-white/5 active:scale-95 transition-all"
                        >
                            <svg className="w-6 h-6 text-green-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" />
                            </svg>
                            Record Video
                        </button>
                    </div>
                    <button
                        onClick={() => {
                            setShowChoice(false);
                            onClose();
                        }}
                        className="text-gray-400 hover:text-white font-medium text-sm transition-colors mt-2"
                    >
                        Cancel
                    </button>
                </div>
            </div>
        );
    }

    return null;
};
